import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, Check } from 'lucide-react';
import { Header } from '../components/Header';

const KundenSetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [customerInfo, setCustomerInfo] = useState(null);
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  useEffect(() => {
    const t = searchParams.get('token');
    if (!t) {
      setError('Kein Token gefunden');
      setVerifying(false);
      return;
    }
    setToken(t);
    verifyToken(t);
  }, [searchParams]);

  const verifyToken = async (t) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/kunden/verify-token/${t}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Ungültiger Link');
      }

      setCustomerInfo(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setVerifying(false);
    }
  };

  const handleResendLink = async () => {
    if (!customerInfo?.email) return;
    
    setResending(true);
    setResendSuccess(false);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/kunden/resend-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: customerInfo.email })
      });

      if (response.ok) {
        setResendSuccess(true);
      }
    } catch (err) {
      // Silent fail
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/kunden/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Fehler beim Setzen des Passworts');
      }

      // Store token
      localStorage.setItem('customer_token', data.token);
      localStorage.setItem('customer_id', data.customer.id);
      localStorage.setItem('customer_email', data.customer.email);
      localStorage.setItem('customer_name', data.customer.name);

      // Redirect to dashboard
      navigate('/kunden/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p>Verifiziere Link...</p>
      </div>
    );
  }

  if (error && !customerInfo) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="pt-32 pb-16">
          <div className="max-w-md mx-auto px-6">
            <div className="bg-white border-2 border-red-200 rounded-3xl p-10 text-center">
              <h1 className="text-2xl font-bold text-red-700 mb-4">Ungültiger Link</h1>
              <p className="text-gray-600 mb-6">{error}</p>
              
              {resendSuccess && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
                  <p className="text-sm text-green-700">
                    Ein neuer Zugangslink wurde versendet. Bitte prüfen Sie Ihr E-Mail-Postfach.
                  </p>
                </div>
              )}
              
              {error.includes('abgelaufen') && (
                <button
                  onClick={handleResendLink}
                  disabled={resending}
                  className="w-full mb-4 px-6 py-3 bg-brand-green hover:bg-brand-green-dark text-white rounded-xl font-semibold disabled:opacity-50"
                >
                  {resending ? 'Wird gesendet...' : 'Link erneut senden'}
                </button>
              )}
              
              <a href="/kunden/login" className="text-brand-green hover:text-brand-green-dark font-semibold underline">
                Zum Login
              </a>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="pt-32 pb-16">
        <div className="max-w-md mx-auto px-6">
          <div className="bg-white border-2 border-gray-100 rounded-3xl p-10 shadow-xl">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-brand-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-brand-green" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Passwort setzen</h1>
              <p className="text-gray-600">Hallo {customerInfo?.name}</p>
              <p className="text-sm text-gray-500 mt-1">{customerInfo?.email}</p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">Neues Passwort *</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green"
                    placeholder="Mindestens 8 Zeichen"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">Passwort bestätigen *</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green"
                  placeholder="Passwort wiederholen"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-brand-green hover:bg-brand-green-dark text-white py-4 rounded-xl font-semibold inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
              >
                {loading ? 'Speichern...' : 'Passwort setzen & Anmelden'}
                <Check className="w-5 h-5" />
              </button>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default KundenSetPasswordPage;
